import { BrandSignalSample } from "../components/BrandSignalSampleList";
import { NotificationSignalTypes, SignalMonitoringRecipientTypes } from "applications/signal/constants";
import {
  SignalBrandRate,
  SignalBrandSalesAmountOutput,
  SignalBrandSalesAmount,
  SignalBrandRateOutput,
  SignalMonitoringRecipient,
  SignalUser,
  SignalUserTeam
} from "gql/graphql";
import { groupBy } from "utils/common/arrayUtils";

const CATEGORY_DELIMITER = " / ";
const targetBrandLabel = "対象商品";
const comperableBrandLabel = "競合商品";
const comperableBrandTotalLabel = "競合トータル";
const noValueLabel = "-";
const noTargetBrandSign = "not_target";

/**
 * 表示用にLV1からCATEGORY_DELIMITERで連結した名称を作成
 * @param row
 * @returns
 */
export function categoryNameConcat(row: Record<string, string>) {
  return `${row["lv1name"]}${CATEGORY_DELIMITER}${row["lv2name"]}${CATEGORY_DELIMITER}${row["lv3name"]}${CATEGORY_DELIMITER}${row["lv4name"]}`
}

/**
 * 選択されたカテゴリーに紐づくブランド名リストを取得
 * @param categoryBrandData
 * @param lv4nameList
 * @returns
 */
export function filterBrandDataByCategory(categoryBrandData: any[], lv4nameList: string[]) {
  const brandData = categoryBrandData?.filter(row => lv4nameList.map(item => extractLv4Name(item))
    .includes(row["lv4name"])).map(row => row["brand_name"]) || [];
  return Array.from(new Set(brandData));
}

/**
 * LV4名称を抽出、lv4nameとして使用するときは必ずこの関数を使用する
 * @param concatName
 * @returns
 */
export function extractLv4Name(concatName: string) {
  const parts = concatName.split(CATEGORY_DELIMITER);
  return parts[3] || concatName;
}

/**
 * ブランドごとのサンプル値の最大・最小値を取得
 * @param samples
 * @param signalType
 * @returns
 */
function minAndMaxsampleValues(samples: SignalBrandSalesAmount[] | SignalBrandRate[], signalType: number) {
  let sortedBrandSignalSamples: number[] = [];
  if ([NotificationSignalTypes.SalesAmount.ordinal, NotificationSignalTypes.SalesQuantity.ordinal].includes(signalType)) {
    sortedBrandSignalSamples = samples.map((result) => (result as SignalBrandSalesAmount).brand_sales_amount)
      .sort((a, b) => b - a); // Sort in descending order

  } else if ([NotificationSignalTypes.ShareRate.ordinal, NotificationSignalTypes.SalesRate.ordinal].includes(signalType)) {
    sortedBrandSignalSamples = samples.map((result) => (result as SignalBrandRate).brand_rate)
      .sort((a, b) => b - a); // Sort in descending order
  } else {
    return [0.0, 0.0];
  }
  return [sortedBrandSignalSamples[sortedBrandSignalSamples.length - 1], sortedBrandSignalSamples[0]];
}

/**
 * ブランドごとの最大・最小値を選択して表示するためのデータを整形
 * @param signalType
 * @param response
 * @returns BrandSignalSample[]
 * @note BrandSignalSampleはcomponents/BrandSignalSampleList/index.tsxで定義している、主にそのコンポーネントで使用する
 */
export function transformBrandSignalResponseToSampleData(signalType: number, response: any): BrandSignalSample[] {
  const brandedSampleData: BrandSignalSample[] = [];
  let result;
  switch (signalType) {
    case NotificationSignalTypes.SalesAmount.ordinal:
    case NotificationSignalTypes.SalesQuantity.ordinal:
      result = response?.getSignalBrandSalesAmount as SignalBrandSalesAmountOutput;
      break;
    case NotificationSignalTypes.SalesRate.ordinal:
      result = response?.getSignalBrandSalesRate as SignalBrandRateOutput;
      break;
    case NotificationSignalTypes.ShareRate.ordinal:
      result = response?.getSignalBrandShareRate as SignalBrandRateOutput;
      break;
    default:
      break;
  }
  if (result) {
    const targetBrandResult = result.target_brand_result;
    const comperetiveBrandResults = result.comperetive_brand_results;
    const [minThreshold, maxThreshold] = minAndMaxsampleValues(targetBrandResult, signalType);
    brandedSampleData.push({
      entityLabel: targetBrandLabel,
      brandName: targetBrandResult[0].brand_name,
      minThreshold: minThreshold,
      maxThreshold: maxThreshold,
    });
    // 競合トータル、一旦シェア率は0でセット
    brandedSampleData.push({
      entityLabel: comperableBrandTotalLabel,
      brandName: noValueLabel,
      minThreshold: 0,
      maxThreshold: 0,
    });
    const brandGrouped = groupBy(
      (comperetiveBrandResults as SignalBrandRate[]) || [],
      data => data.brand_name
    ) || new Map<string, SignalBrandRate[]>();
    let index = 1;
    Object.entries(brandGrouped).forEach(([brandName, brandSample]) => {
      if (brandName !== noTargetBrandSign && brandSample) {
        const [minThreshold, maxThreshold] = minAndMaxsampleValues(brandSample, signalType);
        brandedSampleData.push({
          entityLabel: `${comperableBrandLabel}-${index++}`,
          brandName: brandName,
          minThreshold: minThreshold,
          maxThreshold: maxThreshold,
        });
      }
    });
    const comparebleBrandSamples = brandedSampleData.slice(2, brandedSampleData.length);
    const totalMinThreshold = comparebleBrandSamples.reduce((sum, sample) => sum + sample.minThreshold, 0);
    const totalMaxThreshold = comparebleBrandSamples.reduce((sum, sample) => sum + sample.maxThreshold, 0);
    brandedSampleData[1] = {
      entityLabel: comperableBrandTotalLabel,
      brandName: noValueLabel,
      minThreshold: totalMinThreshold / comparebleBrandSamples.length,
      maxThreshold: totalMaxThreshold / comparebleBrandSamples.length,
    }
  }
  return brandedSampleData;
}

const GROUP_MEMBER_ID_DELIMITER = ": ";
const USER_TEAM_RECIPIENT_OPTION_PREFIX = "チーム: ";

/**
 * SignalMonitoringRecipientの配列を受信者Selectのoptions形式に変換
 * @param registered
 * @param teamRecipients
 * @param memberRecipients
 * @returns
 */
export function signalMonitoringRecipientOptionsFormatter(
  registered: SignalMonitoringRecipient[], teamRecipients: SignalUserTeam[], memberRecipients: SignalUser[]
) {
  return registered.map(recipient => {
    if (recipient.type === SignalMonitoringRecipientTypes.userTeam) {
      const team = teamRecipients.find(t => t.id === recipient.recipient_model_id);
      return userTeamRecipientOption(team!);
    } else {
      const member = memberRecipients.find(m => m.signal_group_member_id === recipient.recipient_model_id);
      return signalUserOption(member!);
    }
  }).filter(option => option !== null) as string[];
}

/**
 * SignalMonitoringRecipientのoptions形式文字列の配列をSignalMonitoringRecipientの配列に変換
 * @param options
 * @returns
 */
export function signalMonitoringRecipientOptionsToInputFormatter(
  options: string[],
  teamRecipientCandidates: SignalUserTeam[]
): SignalMonitoringRecipient[] {
  return options.map(option => {
    if (option.startsWith(USER_TEAM_RECIPIENT_OPTION_PREFIX)) {
      const teamName = option.replace(USER_TEAM_RECIPIENT_OPTION_PREFIX, "");
      const team = teamRecipientCandidates.find(t => t.name === teamName);
      return {
        type: SignalMonitoringRecipientTypes.userTeam,
        recipient_model_id: team!.id,
      } as SignalMonitoringRecipient;
    } else {
      const parts = option.split(GROUP_MEMBER_ID_DELIMITER);
      const memberId = parts[0];
      return {
        type: SignalMonitoringRecipientTypes.groupMember,
        recipient_model_id: memberId,
      } as SignalMonitoringRecipient;
    }
  });
}

/**
 * SignalUserをグループメンバー|Signal受信者Selectのoptionsで使用する文字列に変換
 * @param user
 * @returns
 */
function signalUserOption(user: SignalUser) {
  return `${user.signal_group_member_id}${GROUP_MEMBER_ID_DELIMITER}${user.last_name} ${user.first_name} (${user.email})`;
}

/**
 * SignalUserの配列をグループメンバー|Signal受信者Selectのoptions形式に変換
 * @param userList
 * @returns string[]
 */
export function signalUserOptionsFormatter(userList: Array<SignalUser>) {
  return userList.map(user => signalUserOption(user));
}

/**
 * グループメンバーSelectのoptions形式からメンバーIDの配列を抽出
 * @param options
 * @returns string[]
 */
export function extractMemberIdsFromOptions(options: string[]) {
  return options.map(option => {
    const parts = option.split(GROUP_MEMBER_ID_DELIMITER);
    return parts[0];
  });
}

/**
 * ユーザーチームを受信者Selectのoptionsで使用する文字列に変換
 * @param team
 * @returns
 */
function userTeamRecipientOption(team: SignalUserTeam) {
  return `${USER_TEAM_RECIPIENT_OPTION_PREFIX}${team.name}`;
}

/**
 * ユーザーチームの配列を受信者Selectのoptions形式に変換
 * @param teams
 * @returns
 */
export function userTeamRecipientOptionFormatter(teams: SignalUserTeam[]) {
  return teams.map(team => userTeamRecipientOption(team));
}
